import { PageHeader } from '@/components/common/PageHeader';
import { PaymentStatus } from '@/features/orders-management/constants';
import { createPaymentApi, updateOrderApi } from '@/features/orders-management/api/dataverse';
import { updateQuote } from '../api/dataverse';
import { CompleteFollowUpModal } from '@/features/dashboard/components/CompleteFollowUpModal';
import { FOLLOWUP_TYPE_MAP } from '@/features/dashboard/constants';
import { useCreateQuoteFollowup } from '@/features/dashboard/hooks';
import { useGetCurrentUser } from '@/hooks/app';
import { ROUTESPATH } from '@/routes';
import { REGION_ALL } from '@/utils/constant/appConstant';
import { exportToCSV, formatDateForCSV, generateCSVFilename } from '@/utils/csv';
import { formatDealerAmount } from '@/utils/currency';
import { PermissionChecker } from '@/utils/permission';
import { Button } from 'antd';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { QuoteFilters, QuoteStats, QuoteTable } from '../components';
import { ConvertedModal, ExpiredModal, FollowUpModal, ProjectModal } from '../components/quotes/status-modals';
import { DEFAULT_PAGE_SIZE, DEFAULT_STARTING_PAGE } from '../constants';
import {
  useAllQuoteCountsByStatus,
  useFollowUpChoiceCount,
  useCloseQuote,
  useConvertQuoteToOrder,
  useExportQuotes,
  useQuoteList,
  useTotalQuotesCount,
  useTotalQuotesValue,
  useUpdateQuote,
  useCreateNote
} from '../hooks';
import { IDataverseQuote, QuoteFollowUpStatus, QuoteStateCode, QuoteStatus } from '../types';
import {
  combineFilterQueries,
  generateAccountFilterQuery,
  generateDateRangeFilterQuery,
  generateProjectTypeFilterQuery,
  generateQuotesSearchQuery,
  generateRegionFilterQuery,
  generateStatusFilterQuery,
} from '../utils';

export const QuotesManagementPage: React.FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    current: DEFAULT_STARTING_PAGE,
    pageSize: DEFAULT_PAGE_SIZE || 10,
    quoteFilter: `statuscode ne ${QuoteStatus.CONVERTED}`,
  });

  // Stores the nextSkipToken from the latest API response, used to fetch the next page
  const [currentNextSkipToken, setCurrentNextSkipToken] = useState<string[]>([]);

  // Get current user permissions
  const { data: currentUserData } = useGetCurrentUser();
  const permissions = currentUserData?.permission;

  // Initialize PermissionChecker
  const permissionChecker = new PermissionChecker(permissions);

  // Permission checks
  const canConvertQuoteToOrder = permissionChecker.canConvertQuoteToOrder();
  const canManageQuotesStatus = permissionChecker.canManageQuotesStatus();
  const canViewFollowup = permissionChecker.canViewFollowup();
  const canManageFollowup = permissionChecker.canManageFollowup();

  // Region filter logic
  const userRegion = currentUserData?.currentUser?.dogma_region;
  const isRegionAll = Boolean(userRegion && userRegion === REGION_ALL);

  const { data: quoteListResponse, isLoading } = useQuoteList({ queryParams });
  const updateQuoteMutation = useUpdateQuote();
  const createNoteMutation = useCreateNote();
  const closeQuoteMutation = useCloseQuote();

  const convertToOrderMutation = useConvertQuoteToOrder();
  const exportQuotesMutation = useExportQuotes();
  const createFollowupMutation = useCreateQuoteFollowup();
  const navigate = useNavigate();
  const quotes = quoteListResponse?.data || [];

  const [selectedQuote, setSelectedQuote] = useState<IDataverseQuote | null>(null);

  const [currentSearchText, setCurrentSearchText] = useState<string>("");
  const [currentStatus, setCurrentStatus] = useState<QuoteStatus | null>(null);
  const [currentRegion, setCurrentRegion] = useState<number | null>(null);

  React.useEffect(() => {
    if (userRegion && !isRegionAll) {
      setCurrentRegion(userRegion);
      setQueryParams((prev: any) => ({
        ...prev,
        current: DEFAULT_STARTING_PAGE,
        nextSkipToken: undefined,
        quoteFilter: combineFilterQueries([
          `statuscode ne ${QuoteStatus.CONVERTED}`,
          generateRegionFilterQuery(userRegion),
        ]) || undefined,
      }));
    }
  }, [userRegion, isRegionAll]);

  const { data: totalCountData, isLoading: isTotalCountLoading } = useTotalQuotesCount(currentRegion);
  const { data: totalValueData, isLoading: isTotalValueLoading } = useTotalQuotesValue(currentRegion);
  const { data } = useAllQuoteCountsByStatus(currentRegion)
  const { data: followupChoiceData } = useFollowUpChoiceCount(currentRegion)
  const [currentAccount, setCurrentAccount] = useState<string | null>(null);
  const [currentDateRange, setCurrentDateRange] = useState<[string, string] | null>(null);
  const [currentProjectType, setCurrentProjectType] = useState<number | null>(null);

  // Modal states
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [convertedModalOpen, setConvertedModalOpen] = useState(false);
  const [expiredModalOpen, setExpiredModalOpen] = useState(false);
  const [completeFollowUpModalOpen, setCompleteFollowUpModalOpen] = useState(false);
  const [isCompletingFollowup, setIsCompletingFollowup] = useState(false);

  const buildFilters = (overrides: {
    searchText?: string;
    status?: QuoteStatus | null;
    region?: number | null;
    account?: string | null;
    dateRange?: [string, string] | null;
    projectType?: number | null;
  }) => {
    const searchText = 'searchText' in overrides ? (overrides.searchText ?? '') : currentSearchText;
    const status = 'status' in overrides ? overrides.status : currentStatus;
    const region = 'region' in overrides ? overrides.region : currentRegion;
    const account = 'account' in overrides ? overrides.account : currentAccount;
    const dateRange = 'dateRange' in overrides ? overrides.dateRange : currentDateRange;
    const projectType = 'projectType' in overrides ? overrides.projectType : currentProjectType;

    const searchQuery = searchText ? generateQuotesSearchQuery(searchText).replace('&$filter=', '') : '';
    const quoteFilter = combineFilterQueries([
      `statuscode ne ${QuoteStatus.CONVERTED}`,
      searchQuery ? `(${searchQuery})` : '',
      status !== null && status !== undefined ? generateStatusFilterQuery(status) : '',
      region !== null ? generateRegionFilterQuery(region!) : '',
      account ? generateAccountFilterQuery(account) : '',
      dateRange ? generateDateRangeFilterQuery(dateRange[0], dateRange[1]) : '',
      projectType !== null && projectType !== undefined ? generateProjectTypeFilterQuery(projectType) : '',
    ]);

    return {
      quoteFilter: quoteFilter || undefined,
    };
  };

  const resetToFirstPage = (extra?: Record<string, unknown>) => {
    setCurrentNextSkipToken([]);
    setQueryParams({ current: DEFAULT_STARTING_PAGE, pageSize: queryParams.pageSize, nextSkipToken: undefined, ...extra });
  };

  const handleSearchTextChange = (searchText: string) => {
    setCurrentSearchText(searchText);
    const { quoteFilter } = buildFilters({ searchText });
    resetToFirstPage({ quoteFilter });
  };

  const handleStatusFilterChange = (status: QuoteStatus | null) => {
    setCurrentStatus(status);
    const { quoteFilter } = buildFilters({ status });
    resetToFirstPage({ quoteFilter });
  };

  const handleRegionFilterChange = (region: number | null) => {
    setCurrentRegion(region);
    const { quoteFilter } = buildFilters({ region });
    resetToFirstPage({ quoteFilter });
  };

  const handleAccountFilterChange = (accountId: string | null) => {
    setCurrentAccount(accountId);
    const { quoteFilter } = buildFilters({ account: accountId });
    resetToFirstPage({ quoteFilter });
  };

  const handleDateRangeFilterChange = (dateRange: [string, string] | null) => {
    setCurrentDateRange(dateRange);
    const { quoteFilter } = buildFilters({ dateRange });
    resetToFirstPage({ quoteFilter });
  };

  const handleProjectTypeFilterChange = (projectType: number | null) => {
    setCurrentProjectType(projectType);
    const { quoteFilter } = buildFilters({ projectType });
    resetToFirstPage({ quoteFilter });
  };

  const handleExportCSV = () => {
    exportQuotesMutation.mutate({ quoteFilter: queryParams.quoteFilter }, {
      onSuccess: (data) => {
        const rows = data?.data || [];
        const csvData = rows.map((q: any) => ({
          'Quote Number': q.quotenumber || '-',
          'Customer': q['_dogma_customer_value@OData.Community.Display.V1.FormattedValue'] || '-',
          'Account': q['_customerid_value@OData.Community.Display.V1.FormattedValue'] || '-',
          'Project Reference': q.dogma_projectreference || '-',
          'Status': q['statuscode@OData.Community.Display.V1.FormattedValue'] || '-',
          'Created Date': q.createdon ? formatDateForCSV(q.createdon) : '-',
          'Total Amount': q.totalamount != null ? formatDealerAmount(q.totalamount, q.dogma_DealersCurrency, q.dogma_exchangerate ?? q.dogma_DealersCurrency?.dogma_exchangerate ?? 1) : '-',
        }));
        const columns = [
          { key: 'Quote Number' as const, header: 'Quote Number' },
          { key: 'Customer' as const, header: 'Customer' },
          { key: 'Account' as const, header: 'Account' },
          { key: 'Project Reference' as const, header: 'Project Reference' },
          { key: 'Status' as const, header: 'Status' },
          { key: 'Created Date' as const, header: 'Created Date' },
          { key: 'Total Amount' as const, header: 'Total Amount' },
        ];
        exportToCSV(csvData, columns, generateCSVFilename('quotes'));
      },
    });
  };

  const handleApplyAllFilters = ({
    status,
    region,
    account,
    dateRange,
    projectType,
  }: {
    status: QuoteStatus | null;
    region: number | null;
    account: string | null;
    dateRange: [string, string] | null;
    projectType: number | null;
  }) => {
    setCurrentStatus(status);
    setCurrentRegion(region);
    setCurrentAccount(account);
    setCurrentDateRange(dateRange);
    setCurrentProjectType(projectType);
    const { quoteFilter } = buildFilters({ status, region, account, dateRange, projectType });
    resetToFirstPage({ quoteFilter });
  };

  const handleClearAllFilters = () => {
    setCurrentSearchText('');
    setCurrentStatus(null);
    setCurrentRegion(isRegionAll ? null : (userRegion || null));
    setCurrentAccount(null);
    setCurrentDateRange(null);
    setCurrentProjectType(null);
    resetToFirstPage({ quoteFilter: `statuscode ne ${QuoteStatus.CONVERTED}` });
  };

  const handleStatusChange = (_quote: IDataverseQuote, _newStatus: QuoteStatus) => {
    // Note: FOLLOW_UP_NEEDED and PROJECT are from old QuoteStatus enum
    // For now, we're not handling these as they need to be added to Dataverse enum
    // if (newStatus === QuoteStatus.FOLLOW_UP_NEEDED) {
    //   setSelectedQuote(quote);
    //   setShowFollowUpModal(true);
    //   return;
    // }
    // if (newStatus === QuoteStatus.PROJECT) {
    //   setSelectedQuote(quote);
    //   setProjectValue((quote.totalamount || 0).toString());
    //   setShowProjectModal(true);
    //   return;
    // }


  };


  const handleMarkAsRead = () => {
    // Mark as read logic - Dataverse doesn't have isNew field yet
    // TODO: MARK AS READ
  };

  const handlePageChange = (page: number, pageSize: number) => {
    if (page > queryParams.current) {
      // Going NEXT: take the token from the current response
      const nextToken = quoteListResponse?.nextSkipToken;
      if (!nextToken) return;
      // Store token at the current page index so we can retrieve it when going back
      // e.g. tokenHistory[1] = token from page 1 → used to fetch page 2
      setCurrentNextSkipToken((prev) => {
        const updated = [...prev];
        updated[queryParams.current] = nextToken;
        return updated;
      });
      setQueryParams((prev: any) => ({
        ...prev,
        current: page,
        pageSize,
        nextSkipToken: nextToken,
      }));
    } else {
      // Going PREV: look up the stored token for page-1
      // e.g. to go to page 2 → use tokenHistory[1] (stored when on page 1)
      const prevToken = page > 1 ? currentNextSkipToken[page - 1] : undefined;
      setQueryParams((prev: any) => ({
        ...prev,
        current: page,
        pageSize,
        nextSkipToken: prevToken,
      }));
    }
  };

  // Modal handlers
  const handleFollowUpClick = (quote: IDataverseQuote) => {
    setSelectedQuote(quote);
    setFollowUpModalOpen(true);
  };

  const handleProjectClick = (quote: IDataverseQuote) => {
    setSelectedQuote(quote);
    setProjectModalOpen(true);
  };

  const handleConvertedClick = (quote: IDataverseQuote) => {
    setSelectedQuote(quote);
    setConvertedModalOpen(true);
  };

  const handleExpiredClick = (quote: IDataverseQuote) => {
    setSelectedQuote(quote);
    setExpiredModalOpen(true);
  };

  // Modal confirm handlers
  const handleFollowUpConfirm = async (formData: any) => {
    if (!selectedQuote) return;

    const dogmaType = FOLLOWUP_TYPE_MAP[formData.followUpType] || 1;

    const payload = {
      dogma_name: `Follow Up - ${formData.followUpType}`,
      'dogma_Quote@odata.bind': `/quotes(${selectedQuote.quoteid})`,
      dogma_notes: formData.remarks || '',
      dogma_type: dogmaType,
      dogma_followupdate: formData.followUpDate?.format('YYYY-MM-DD') || '',
    };

    createFollowupMutation.mutate(payload, {
      onSuccess: () => {
        setFollowUpModalOpen(false);
        setSelectedQuote(null);
      },
    });
    updateQuoteMutation.mutate({
      id: selectedQuote.quoteid,
      payload: {
        dogma_followup: QuoteFollowUpStatus.IN_PROGRESS,
      },
    });
  };

  const handleProjectConfirm = async (remarks?: string) => {
    if (!selectedQuote) return;
    updateQuoteMutation.mutate({
      id: selectedQuote.quoteid,
      payload: {
        statuscode: QuoteStatus.FOLLOW_UP_NEEDED,
        statecode: QuoteStateCode.DRAFT,
        description: remarks
      },
    });
    setProjectModalOpen(false);
    setSelectedQuote(null);
  };

  const handleConvertedConfirm = async (formData: { remarks?: string; destination: 'email' | 'sms'; requestdeliveryby: string }) => {
    if (!selectedQuote) return;
    try {
      const paymentTermsCode = selectedQuote.customerid_account?.paymenttermscode ?? selectedQuote.paymenttermscode;

      // Step 1: Patch quote to Live
      await updateQuote({ id: selectedQuote.quoteid, payload: { statuscode: QuoteStatus.LIVE, statecode: QuoteStateCode.ACTIVE } });

      // Step 2: Create order
      const orderResult = await convertToOrderMutation.mutateAsync({
        quoteid: selectedQuote.quoteid,
        dogma_paymentstatus: paymentTermsCode === 1 ? PaymentStatus.PAYMENT_LINK_SENT : PaymentStatus.CREDIT,
        description: formData.remarks,
        requestdeliveryby: formData.requestdeliveryby,
      });

      // Step 3: Patch quote to Converted
      await updateQuote({ id: selectedQuote.quoteid, payload: { statuscode: QuoteStatus.CONVERTED } });

      // Step 4: If payment link account, create payment and set order to Awaiting Payment
      if (paymentTermsCode === 1 && orderResult?.orderId) {
        const exchangeRate = (orderResult as any)?.orderDetails?.dogma_exchangerate ?? selectedQuote.dogma_exchangerate ?? 1;
        const vssTotal = (orderResult as any)?.orderDetails?.dogma_vsstotalamount ?? 0;
        await createPaymentApi({
          total: Math.round(vssTotal * exchangeRate * 100),
          currency: selectedQuote.dogma_DealersCurrency?.dogma_isocurrencycode ?? 'GBP',
          userPaymentId: `${(orderResult as any)?.dogma_ordernumber ?? (orderResult as any)?.ordernumber ?? ''}-${selectedQuote.customerid_account?.name ?? ''}`,
          email: selectedQuote.customerid_contact?.emailaddress1 ?? '',
          phone: selectedQuote.customerid_contact?.telephone1 ?? '',
          orderid: orderResult.orderId,
          destination: formData.destination,
        });
        await updateOrderApi({ id: orderResult.orderId, statuscode: 736000003 });
      }

      setConvertedModalOpen(false);
      setSelectedQuote(null);
    } catch {
      // mutations handle their own error toasts
    }
  };

  const handleExpiredConfirm = async (remarks?: string) => {
    if (!selectedQuote) return;
    closeQuoteMutation.mutate({
      quoteid: selectedQuote.quoteid,
      subject: remarks || "Quote Expired",
      Status: QuoteStatus.EXPIRED,
    });
    setExpiredModalOpen(false);
    setSelectedQuote(null);
  };

  const handleCompleteFollowUp = async (quote: IDataverseQuote) => {
    setSelectedQuote(quote);
    setCompleteFollowUpModalOpen(true);
  };

  const handleCompleteFollowUpConfirm = async () => {
    if (!selectedQuote) {
      toast.error('No quote selected');
      return;
    }

    setIsCompletingFollowup(true);

    try {
      await updateQuoteMutation.mutateAsync({
        id: selectedQuote.quoteid,
        payload: {
          dogma_followup: QuoteFollowUpStatus.RESOLVED,
        },
      });

      toast.success('Follow-up marked as complete successfully');
      setCompleteFollowUpModalOpen(false);
      setSelectedQuote(null);
    } catch (error) {
      toast.error('Failed to mark follow-up as complete');
    } finally {
      setIsCompletingFollowup(false);
    }
  };

  const isStatsLoadingCombined = useMemo(
    () => isTotalCountLoading || isTotalValueLoading,
    [isTotalCountLoading, isTotalValueLoading]
  );

  return (
    <div className="p-4 space-y-4">
      {/* Page Header */}
      <PageHeader
        title="Quote Management"
        subtitle="Manage and track all customer quotes, from draft to conversion"
        breadcrumbs={[
          { label: 'Home', path: ROUTESPATH.DASHBOARD },
          { label: 'Quote Management' },
        ]}
        actions={
          <Button
            icon={<IoCloudDownloadOutline />}
            loading={exportQuotesMutation.isPending}
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
        }
      />

      <QuoteStats
        loading={isStatsLoadingCombined}
        statusCounts={data?.value}
        totalCount={totalCountData?.value[0]?.count ?? 0}
        totalValue={totalValueData?.value[0]?.totalValue ?? 0}
        followupCount={followupChoiceData?.value[0]?.count ?? 0}
      />

      <QuoteFilters
        currentSearchText={currentSearchText}
        currentStatus={currentStatus}
        currentRegion={currentRegion}
        currentAccount={currentAccount}
        currentDateRange={currentDateRange}
        currentProjectType={currentProjectType}
        isRegionAll={isRegionAll}
        userRegion={userRegion}
        onSearchChange={handleSearchTextChange}
        onStatusChange={handleStatusFilterChange}
        onRegionChange={handleRegionFilterChange}
        onAccountChange={handleAccountFilterChange}
        onDateRangeChange={handleDateRangeFilterChange}
        onProjectTypeChange={handleProjectTypeFilterChange}
        onApplyFilters={handleApplyAllFilters}
        onClearAll={handleClearAllFilters}
      />
      <div className="rounded-lg">
        <QuoteTable
          quotes={quotes}
          isLoading={isLoading}
          current={queryParams.current}
          pageSize={queryParams.pageSize}
          total={quoteListResponse?.totalRecordCountLimitExceeded ? undefined : (quoteListResponse?.total ?? quoteListResponse?.count)}
          nextLink={quoteListResponse?.nextSkipToken}
          totalRecordCountLimitExceeded={true}
          onPageChange={handlePageChange}
          onMarkAsRead={handleMarkAsRead}
          onView={(record) => {
            navigate(ROUTESPATH.QUOTE_DETAIL.replace(":id", record.quoteid))
          }}
          onFollowUp={(quote) => {
            setSelectedQuote(quote);
            setFollowUpModalOpen(true);
          }}
          onSend={() => { }}
          onConvert={handleConvertedClick}
          onStatusChange={(quote, status) => handleStatusChange(quote, status)}
          onAddNote={(quote, note) => {
            createNoteMutation.mutate({
              entityType: 'quote',
              recordId: quote.quoteid,
              subject: 'Customer Note',
              noteText: note,
            });
          }
          }
          onFollowUpClick={handleFollowUpClick}
          onProjectClick={handleProjectClick}
          onConvertedClick={handleConvertedClick}
          onExpiredClick={handleExpiredClick}
          onCompleteFollowUp={handleCompleteFollowUp}
          canConvertQuoteToOrder={canConvertQuoteToOrder}
          canManageQuotesStatus={canManageQuotesStatus}
          canViewFollowup={canViewFollowup}
          canManageFollowup={canManageFollowup}
        />
      </div>

      {/* Status Change Modals */}
      <FollowUpModal
        open={followUpModalOpen}
        onClose={() => {
          setFollowUpModalOpen(false);
          setSelectedQuote(null);
        }}
        onConfirm={handleFollowUpConfirm}
        isLoading={createFollowupMutation.isPending}
      />

      <ProjectModal
        open={projectModalOpen}
        onClose={() => {
          setProjectModalOpen(false);
          setSelectedQuote(null);
        }}
        onConfirm={handleProjectConfirm}
        isLoading={closeQuoteMutation.isPending}
      />

      <ConvertedModal
        open={convertedModalOpen}
        onClose={() => {
          setConvertedModalOpen(false);
          setSelectedQuote(null);
        }}
        onConfirm={handleConvertedConfirm}
        isLoading={convertToOrderMutation.isPending}
        showPaymentDestination={
          (selectedQuote?.customerid_account?.paymenttermscode ?? selectedQuote?.paymenttermscode) === 1
        }
        quoteId={selectedQuote?.quoteid}
        region={selectedQuote?.dogma_region ?? null}
      />

      <ExpiredModal
        open={expiredModalOpen}
        onClose={() => {
          setExpiredModalOpen(false);
          setSelectedQuote(null);
        }}
        onConfirm={handleExpiredConfirm}
        isLoading={closeQuoteMutation.isPending}
      />

      <CompleteFollowUpModal
        open={completeFollowUpModalOpen}
        onClose={() => {
          setCompleteFollowUpModalOpen(false);
          setSelectedQuote(null);
        }}
        onConfirm={handleCompleteFollowUpConfirm}
        isLoading={isCompletingFollowup}
      />
    </div>
  );
};

export default QuotesManagementPage;





